import React, { useEffect, useState } from 'react';
import { getRuralExchangeStatus } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import '../styles/ExchangeStatusPageNew.css';
import { motion } from 'framer-motion';
import {
  ArrowRightLeft,
  Filter,
  ArrowRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Building2,
  Calendar,
  Hash,
} from 'lucide-react';

/**
 * RuralExchangeStatusPage
 *
 * 地域実習交換申請状況を一覧表示するページ。
 */
function RuralExchangeStatusPage() {
  const [exchangeData, setExchangeData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // フィルター状況
  const [filterCurrentTerm, setFilterCurrentTerm] = useState('');
  const [filterTargetTerm, setFilterTargetTerm] = useState('');
  const [filterCurrentInstitution, setFilterCurrentInstitution] = useState('');
  const [filterTargetInstitution, setFilterTargetInstitution] = useState('');

  // 並び替え状況
  const [sortKey, setSortKey] = useState('priority');
  const [sortDirection, setSortDirection] = useState('asc');

  const fixedTermOptions = ['1', '2', '3', '4', '5', '6', '7'];
  const [currentInstitutionOptions, setCurrentInstitutionOptions] = useState([]);
  const [targetInstitutionOptions, setTargetInstitutionOptions] = useState([]);

  useEffect(() => {
    async function fetchData() {
      try {
        const data = await getRuralExchangeStatus();
        setExchangeData(data);
        setCurrentInstitutionOptions([
          ...new Set(data.map(item => item.current_institution_name).filter(Boolean))
        ]);
        setTargetInstitutionOptions([
          ...new Set(data.map(item => item.target_institution_name).filter(Boolean))
        ]);
      } catch (err) {
        console.error('Error fetching rural exchange status:', err);
        setError('データの取得に失敗しました。');
      } finally {
        setIsLoading(false);
      }
    }
    fetchData();
  }, []);

  const filteredData = exchangeData.filter(item => {
    const matchCurrentTerm =
      filterCurrentTerm === '' || String(item.current_term_id) === filterCurrentTerm;
    const matchTargetTerm =
      filterTargetTerm === '' || String(item.target_term_id) === filterTargetTerm;
    const matchCurrentInstitution =
      filterCurrentInstitution === '' ||
      (item.current_institution_name && item.current_institution_name === filterCurrentInstitution);
    const matchTargetInstitution =
      filterTargetInstitution === '' ||
      (item.target_institution_name && item.target_institution_name === filterTargetInstitution);
    return matchCurrentTerm && matchTargetTerm && matchCurrentInstitution && matchTargetInstitution;
  });

  const sortedData = [...filteredData].sort((a, b) => {
    let aValue = a[sortKey];
    let bValue = b[sortKey];
    if (sortKey === 'current_term_id' || sortKey === 'target_term_id' || sortKey === 'priority') {
      aValue = Number(aValue);
      bValue = Number(bValue);
    } else {
      aValue = aValue ? aValue.toLowerCase() : '';
      bValue = bValue ? bValue.toLowerCase() : '';
    }
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (key) => {
    if (sortKey !== key) return <ArrowUpDown size={14} className="sort-icon" />;
    return sortDirection === 'asc'
      ? <ArrowUp size={14} className="sort-icon text-blue-600" />
      : <ArrowDown size={14} className="sort-icon text-blue-600" />;
  };

  if (isLoading) {
    return <LoadingSpinner text="データを読み込んでいます..." />;
  }

  if (error) {
    return (
      <div className="exchange-status-page">
        <div className="exchange-container">
          <div className="error-container">
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="exchange-status-page">
      <div className="exchange-container">
        <div className="page-header">
          <h1 className="page-title">
            <ArrowRightLeft className="text-blue-600" size={32} />
            地域実習交換申請状況
          </h1>
          <p className="page-description">
            現在提出されている地域実習交換申請の一覧を確認できます。
          </p>
        </div>

        <motion.section
          className="filter-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <h2 className="filter-title">
            <Filter size={20} />
            フィルター
          </h2>
          <div className="filter-grid">
            <div className="filter-item">
              <label htmlFor="currentTerm" className="filter-label">現在ターム</label>
              <select
                id="currentTerm"
                className="filter-select"
                value={filterCurrentTerm}
                onChange={e => setFilterCurrentTerm(e.target.value)}
              >
                <option value="">全て</option>
                {fixedTermOptions.map(term => (
                  <option key={term} value={term}>{term}</option>
                ))}
              </select>
            </div>
            <div className="filter-item">
              <label htmlFor="currentInstitution" className="filter-label">現在病院</label>
              <select
                id="currentInstitution"
                className="filter-select"
                value={filterCurrentInstitution}
                onChange={e => setFilterCurrentInstitution(e.target.value)}
              >
                <option value="">全て</option>
                {currentInstitutionOptions.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
            <div className="filter-item">
              <label htmlFor="targetTerm" className="filter-label">希望ターム</label>
              <select
                id="targetTerm"
                className="filter-select"
                value={filterTargetTerm}
                onChange={e => setFilterTargetTerm(e.target.value)}
              >
                <option value="">全て</option>
                {fixedTermOptions.map(term => (
                  <option key={term} value={term}>{term}</option>
                ))}
              </select>
            </div>
            <div className="filter-item">
              <label htmlFor="targetInstitution" className="filter-label">希望病院</label>
              <select
                id="targetInstitution"
                className="filter-select"
                value={filterTargetInstitution}
                onChange={e => setFilterTargetInstitution(e.target.value)}
              >
                <option value="">全て</option>
                {targetInstitutionOptions.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
          </div>
        </motion.section>

        <motion.section
          className="table-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          {sortedData.length > 0 ? (
            <div className="table-container">
              <table className="exchange-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('priority')}>
                      <div className="flex items-center gap-1">
                        <Hash size={16} />
                        優先度
                        {renderSortIcon('priority')}
                      </div>
                    </th>
                    <th onClick={() => handleSort('current_term_id')}>
                      <div className="flex items-center gap-1">
                        <Calendar size={16} />
                        現在ターム
                        {renderSortIcon('current_term_id')}
                      </div>
                    </th>
                    <th onClick={() => handleSort('current_institution_name')}>
                      <div className="flex items-center gap-1">
                        <Building2 size={16} />
                        現在病院・科
                        {renderSortIcon('current_institution_name')}
                      </div>
                    </th>
                    <th className="arrow-cell"></th>
                    <th onClick={() => handleSort('target_term_id')}>
                      <div className="flex items-center gap-1">
                        <Calendar size={16} />
                        希望ターム
                        {renderSortIcon('target_term_id')}
                      </div>
                    </th>
                    <th onClick={() => handleSort('target_institution_name')}>
                      <div className="flex items-center gap-1">
                        <Building2 size={16} />
                        希望病院・科
                        {renderSortIcon('target_institution_name')}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedData.map(item => (
                    <tr key={item.detail_id}>
                      <td>
                        <span className="priority-badge">{item.priority}</span>
                      </td>
                      <td>
                        <span className="term-badge">{item.current_term_id || '-'}</span>
                      </td>
                      <td>
                        <div className="hospital-info">
                          <span className="hospital-name">{item.current_institution_name || '-'}</span>
                          <span className="dept-name">{item.current_department_name || '-'}</span>
                        </div>
                      </td>
                      <td className="arrow-cell">
                        <ArrowRight size={20} />
                      </td>
                      <td>
                        <span className="term-badge">{item.target_term_id || '-'}</span>
                      </td>
                      <td>
                        <div className="hospital-info">
                          <span className="hospital-name">{item.target_institution_name || '-'}</span>
                          <span className="dept-name">{item.target_department_name || '-'}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-data">
              <p>該当するデータがありません。</p>
            </div>
          )}
        </motion.section>
      </div>
    </div>
  );
}

export default RuralExchangeStatusPage;
